import { APP_USE_LOCALE_FOR_DATE_TIME } from '@app/GlobalVariables/constants';
import { getGlobalVariable } from '@app/GlobalVariables/util';
import { jsonStringify } from '@utils/json';
import { DEBUG_STRING, hasLocalizationDebug } from '@utils/localize';
import { reportCustomError } from '@utils/reportCustomError';
import { currentTZ, getTimeZone } from '@utils/time/timezone';
import { CURRENT_LOCALE } from '@utils/time/util';
import { isValid } from 'date-fns';
import { isEqual, isNil } from 'lodash-es';
import { FC, HTMLAttributes, memo } from 'react';

export interface Props {
  start: Maybe<string | Date>;
  end?: Maybe<string | Date>;
  /** Example: "America/Chicago" */
  timezone?: Maybe<string>;
  /** Display tz like "CST" at end of string */
  showTimezone?: boolean;
  /** Fallback when there is no data present */
  noData?: string;
  /** Use a different html element instead of default */
  as?: string;
  className?: string;
  style?: HTMLAttributes<unknown>['style'];
  hideTimezoneTooltip?: boolean;
  /** Show year in output (2-digit) */
  showYear?: boolean;
  /** Never show year in output, even when dates span different years */
  neverShowYear?: boolean;
}

// Cache for Intl.DateTimeFormat instances (allows GC of formatters)
const dateTimeFormatCache = new Map<string, Intl.DateTimeFormat>();

function getCachedDateTimeFormatter(
  locale: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `${locale}|${jsonStringify(options)}`;
  let formatter = dateTimeFormatCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options);
    dateTimeFormatCache.set(key, formatter);
  }
  return formatter;
}

function padPart(part: Intl.DateTimeFormatPart): string {
  if (
    (part.type === 'month' || part.type === 'day') &&
    part.value.length === 1
  ) {
    return '0' + part.value;
  }
  return part.value;
}

function filterYearParts(parts: Intl.DateTimeFormatPart[]): string {
  return parts
    .filter((part, i, arr) => {
      if (part.type === 'year') {
        return false;
      }
      // Remove the literal separator immediately before a year part
      if (
        part.type === 'literal' &&
        i + 1 < arr.length &&
        arr[i + 1]?.type === 'year'
      ) {
        return false;
      }
      return true;
    })
    .map(padPart)
    .join('');
}

function formatRangeWithPadding(
  formatter: Intl.DateTimeFormat,
  startDate: Date,
  endDate?: Date,
  neverShowYear?: boolean,
): string {
  if (!endDate) {
    if (CURRENT_LOCALE !== 'en-US' && !neverShowYear) {
      // Zero pad might not work for some non-US locales.
      return formatter.format(startDate);
    }
    const parts = formatter.formatToParts(startDate);
    if (neverShowYear) {
      return filterYearParts(parts);
    }
    return parts.map(padPart).join('');
  }

  if (CURRENT_LOCALE !== 'en-US' && !neverShowYear) {
    // Zero pad might not work for some non-US locales.
    return formatter.formatRange(startDate, endDate);
  }

  const parts = formatter.formatRangeToParts(startDate, endDate);
  if (neverShowYear) {
    return filterYearParts(parts);
  }
  return parts.map(padPart).join('');
}

const shouldUseDebug = hasLocalizationDebug();

export function getDateRangeValue(props: Maybe<Props>): string {
  const enableLocale = getGlobalVariable(APP_USE_LOCALE_FOR_DATE_TIME);
  if (shouldUseDebug) {
    return DEBUG_STRING;
  }
  if (!props) {
    return '';
  }
  const {
    start,
    end,
    noData,
    showTimezone,
    showYear,
    neverShowYear,
    timezone,
  } = props;
  const fallback = noData || '';
  try {
    if (isNil(start)) {
      return fallback;
    }
    const startDate = new Date(start);
    let endDate = end ? new Date(end) : undefined;
    if (!isValid(startDate)) {
      return fallback;
    }
    if (end && !isValid(endDate)) {
      endDate = undefined;
    }
    const userLocale = enableLocale ? CURRENT_LOCALE : 'en-US';
    const tz = timezone || currentTZ;
    const options: Intl.DateTimeFormatOptions = {
      month: '2-digit',
      day: '2-digit',
      ...(showYear && { year: '2-digit' }),
      timeZone: tz,
    };
    const formatter = getCachedDateTimeFormatter(userLocale, options);
    let formatted = formatRangeWithPadding(
      formatter,
      startDate,
      endDate,
      neverShowYear,
    );
    if (!timezone && showTimezone !== false) {
      const localTzShort = getTimeZone(startDate, currentTZ, 'short');
      formatted += ` ${localTzShort}`;
    }
    return formatted;
  } catch (err: anyOk) {
    reportCustomError(err);
    return fallback;
  }
}

const DateRangeValueRaw: FC<Props> = (props) => {
  const El = (props.as || 'span') as anyOk;
  return (
    <El className={props.className} style={props.style}>
      {getDateRangeValue(props)}
    </El>
  );
};

export const DateRangeValue = memo(DateRangeValueRaw, (prev, next) =>
  isEqual(prev, next),
) as typeof DateRangeValueRaw;
